// driverGlossary.js — short, plain-English explanations for the
// keyDriver chips on the Readiness card.
//
// Each chip the AI emits is matched against ordered patterns;
// first match wins. Returns null if no entry matches — the chip
// just stays non-tappable in that case.
//
// Keep entries dense: ~2-3 sentences with 1 concrete number where
// possible. This is a glossary, not an essay.

const ENTRIES = [
  {
    match: /acwr/i,
    title: "Acute : Chronic Workload Ratio",
    body: "Last 7 days of training volume ÷ daily average of the last 28 days. **0.8–1.3** = sweet spot, **>1.5** = spike (injury risk ~2–4× higher in research), **<0.8** = detraining. Gold-standard overtraining predictor in sports science (Gabbett, originally rugby + AFL).",
  },
  {
    match: /\bhrv\b|heart rate variability/i,
    title: "HRV — Heart Rate Variability",
    body: "Variation in beat-to-beat intervals when at rest. Higher = stronger parasympathetic (recovery) tone. Today's value is compared to your 14-day baseline; a drop of >15% from the 3-day trend signals fatigue, illness, or under-recovery before you consciously feel it.",
  },
  {
    match: /\brhr\b|resting heart|resting hr/i,
    title: "RHR — Resting Heart Rate",
    body: "Lowest stable HR seen in the last 24h. **5+ bpm above your 14-day baseline** is a classic early signal of overreaching, brewing illness, or sleep debt — often shows up a day or two before HRV reacts.",
  },
  {
    match: /sleep debt|sleep deficit/i,
    title: "Sleep debt",
    body: "Cumulative hours below an 8h target over the last 7 nights. **Three consecutive bad nights** crush performance and recovery far more than a single bad one does — debt compounds. >7h cumulative debt is a strong bias toward EASY/REST.",
  },
  {
    match: /^sleep\b|\bsleep$|sleep ok|good sleep/i,
    title: "Sleep",
    body: "Total time asleep plus deep+REM (the recovery-critical phases). **<6h or <1.5h combined deep+REM** significantly impairs muscle protein synthesis and CNS recovery. The card compares last night to your 7-day average.",
  },
  {
    match: /recovery|h\s*recovery|hour.*recover|h\s*since|hours? since/i,
    title: "Recovery time",
    body: "Hours since your last logged workout. **48h+** between hard sessions on the same muscle groups is the baseline for full recovery; less is fine for opposing groups or low-intensity work.",
  },
  {
    match: /carbs?\b/i,
    title: "Carbohydrates on board",
    body: "Total carbs consumed today so far. Hard sessions perform better with **~1g/kg bodyweight** in the 2–4h pre-workout window. Empty tank is fine for EASY/MEDIUM but caps strength output and risks early bonking on long sessions.",
  },
  {
    match: /protein/i,
    title: "Protein intake",
    body: "Total protein today. **~25–30g per meal** crosses the leucine threshold for maximal muscle protein synthesis; daily target ~1.6–2.2g/kg bodyweight for recomposition. The chip flags if today is on track or short.",
  },
  {
    match: /fuel|kcal|calorie/i,
    title: "Fuel state",
    body: "Total calories consumed today so far. Training in a deep deficit risks muscle catabolism; a small surplus on training days supports adaptation. Aim for **≥80% of TDEE** before a hard session.",
  },
  {
    match: /steps?\b/i,
    title: "Steps / passive load",
    body: "Total steps over the last 24–48h. **>15k steps** adds meaningful lower-body load on top of training — often the missing piece when legs feel cooked despite a light gym session.",
  },
  {
    match: /weight|hydration|overnight/i,
    title: "Body weight / hydration",
    body: "Overnight drop **>1.5kg** is almost always fluid loss (dehydration, glycogen depletion) — HRV drops mechanically when you're dry, independent of actual fatigue. A sustained 7-day downtrend during a deficit can suppress recovery.",
  },
  {
    match: /max hr|peak hr|avg hr|heart rate/i,
    title: "Heart-rate intensity",
    body: "From your Apple Watch during the last session. Sustained **>170 bpm for over an hour** signals systemic cardiovascular load that takes longer to clear than the muscle work alone.",
  },
  {
    match: /sets?\b|volume|tonnage|prs?\b|kg @|^last:/i,
    title: "Last workout load",
    body: "Tonnage (∑ weight × reps) + set count from your last session. **PRs especially tax the nervous system** — multiple PRs in one day leaves you neurally fatigued for 48–72h regardless of muscle soreness.",
  },
  {
    match: /consec|streak|in a row/i,
    title: "Consecutive training days",
    body: "Days in a row with a logged session. **5+ consecutive** raises overreaching risk even with perfect sleep/nutrition. Planned deload days protect adaptation more than they cost.",
  },
  {
    match: /mounjaro|cycle day/i,
    title: "Mounjaro cycle day",
    body: "Days **1–3 post-injection** often bring appetite suppression, GI side effects, and lower energy — readiness will feel 10–15% worse than HRV/sleep predict. The model biases one tier easier on strength work during this window.",
  },
  {
    match: /fasted|fasting/i,
    title: "Fasting state",
    body: "Hours since last meal + whether you're currently in a fasting window. Fasted training is **fine for EASY/MEDIUM** cardio but caps strength output; pair hard sessions with pre-workout carbs to avoid breakdown.",
  },
  {
    match: /trend|3d|7d|14d/i,
    title: "Multi-day trend",
    body: "3-day rolling average compared to the 14-day baseline. A multi-day trend denoises single-day outliers (caffeine, alcohol, sleep position) that would otherwise mislead a snapshot reading.",
  },
];

export function getDriverExplanation(chipLabel) {
  if (!chipLabel || typeof chipLabel !== "string") return null;
  for (const e of ENTRIES) {
    if (e.match.test(chipLabel)) return { title: e.title, body: e.body };
  }
  return null;
}
